import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Phone, PhoneOff, Volume2, VolumeX, Maximize2, Minimize2, Mic, MicOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * VideoCallMode Component
 * Creates an immersive video call experience with lip-sync animation
 * Mobile-optimized for Android/iOS browsers
 */
const VideoCallMode = ({ 
  avatar, 
  isActive, 
  onClose, 
  audioSrc,
  isPlaying,
  onAudioEnd,
  token
}) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lipSyncLevel, setLipSyncLevel] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [audioInitialized, setAudioInitialized] = useState(false);
  
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const animationRef = useRef(null);
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  // Initialize audio context for lip-sync (mobile-compatible)
  const initAudioAnalyser = useCallback(() => {
    if (!audioRef.current || audioInitialized) return;
    
    try {
      // Create audio context on user interaction (required for mobile)
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }
      
      const audioContext = audioContextRef.current;
      
      // Resume if suspended (mobile browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      
      // Only create source once
      if (!sourceRef.current) {
        const source = audioContext.createMediaElementSource(audioRef.current);
        source.connect(analyser);
        analyser.connect(audioContext.destination);
        sourceRef.current = source;
      }
      
      analyserRef.current = analyser;
      setAudioInitialized(true);
    } catch (error) {
      console.error('Audio analyser init error:', error);
      // Fallback: use simple animation without analyser
      setAudioInitialized(true);
    }
  }, [audioInitialized]);

  // Lip-sync animation loop
  const animateLipSync = useCallback(() => {
    if (!isPlaying) {
      setLipSyncLevel(0);
      return;
    }
    
    if (analyserRef.current) {
      const analyser = analyserRef.current;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(dataArray);
      
      // Focus on speech frequencies (300Hz - 3000Hz for human voice)
      const speechRange = dataArray.slice(2, 30);
      const average = speechRange.reduce((a, b) => a + b, 0) / speechRange.length;
      
      // Normalize to 0-1 range
      const normalizedLevel = Math.min(1, average / 128);
      setLipSyncLevel(normalizedLevel);
    } else {
      // Fallback animation without audio analysis
      const time = Date.now() / 100;
      const fakeLevel = 0.3 + Math.sin(time) * 0.3 + Math.sin(time * 2.3) * 0.2;
      setLipSyncLevel(Math.max(0, Math.min(1, fakeLevel)));
    }
    
    animationRef.current = requestAnimationFrame(animateLipSync);
  }, [isPlaying]);

  // Start lip-sync when playing
  useEffect(() => {
    if (isPlaying) {
      animateLipSync();
    } else {
      setLipSyncLevel(0);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, animateLipSync]);

  // Handle audio source changes
  useEffect(() => {
    if (audioSrc && audioRef.current) {
      audioRef.current.src = audioSrc;
      
      // Initialize audio analyser on first play
      if (!audioInitialized) {
        initAudioAnalyser();
      }
      
      // Resume audio context if needed (mobile)
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume();
      }
      
      audioRef.current.play().catch(err => {
        console.error('Audio play error:', err);
      });
    }
  }, [audioSrc, audioInitialized, initAudioAnalyser]);

  // Call timer
  useEffect(() => {
    if (isActive && isConnected) {
      timerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isActive, isConnected]);

  // Connect animation
  useEffect(() => {
    if (isActive) {
      setTimeout(() => setIsConnected(true), 1000);
    } else {
      setIsConnected(false);
      setCallDuration(0);
      setLipSyncLevel(0);
    }
  }, [isActive]);

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle fullscreen (mobile-compatible)
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (containerRef.current?.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        } else if (containerRef.current?.webkitRequestFullscreen) {
          await containerRef.current.webkitRequestFullscreen();
        }
        setIsFullscreen(true);
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  };

  // Handle mute
  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
    setIsMuted(!isMuted);
  };

  // Handle user interaction to init audio (mobile requirement)
  const handleUserInteraction = () => {
    if (!audioInitialized) {
      initAudioAnalyser();
    }
    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume();
    }
  };

  if (!isActive) return null;

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-[100] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col"
      data-testid="video-call-mode"
      onClick={handleUserInteraction}
    >
      {/* Header - Mobile optimized */}
      <div className="absolute top-0 left-0 right-0 p-3 sm:p-4 flex justify-between items-center z-10 bg-gradient-to-b from-black/60 to-transparent">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`} />
          <span className="text-white font-medium text-sm sm:text-base">
            {isConnected ? 'Live' : 'Connecting...'}
          </span>
          {isConnected && (
            <span className="text-white/60 text-xs sm:text-sm">{formatDuration(callDuration)}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            onClick={toggleFullscreen}
            className="p-1.5 sm:p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 sm:p-2 rounded-full bg-white/10 hover:bg-red-500/50 text-white transition-colors"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>

      {/* Main Avatar Display - Mobile optimized */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="relative">
          {/* Outer Glow Ring */}
          <div 
            className={`absolute inset-[-15px] sm:inset-[-20px] rounded-full transition-all duration-100 ${isPlaying ? 'opacity-100' : 'opacity-30'}`}
            style={{
              background: `radial-gradient(circle, rgba(255,165,0,${0.3 + lipSyncLevel * 0.4}) 0%, transparent 70%)`,
              transform: `scale(${1 + lipSyncLevel * 0.15})`,
            }}
          />
          
          {/* Sound Wave Rings */}
          {isPlaying && [...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute inset-[-25px] sm:inset-[-30px] rounded-full border border-orange-500/30 animate-ping"
              style={{
                animationDelay: `${i * 300}ms`,
                animationDuration: '2s',
              }}
            />
          ))}

          {/* Avatar Container - Responsive sizing */}
          <div 
            className="relative w-48 h-48 sm:w-64 sm:h-64 md:w-80 md:h-80 rounded-full overflow-hidden shadow-2xl"
            style={{
              boxShadow: isPlaying 
                ? `0 0 40px rgba(255,165,0,${0.5 + lipSyncLevel * 0.3}), 0 0 80px rgba(255,165,0,0.2)`
                : '0 0 30px rgba(255,165,0,0.3)',
            }}
          >
            {/* Avatar Image */}
            <img
              src={avatar?.image_url}
              alt={avatar?.display_name}
              className="w-full h-full object-cover"
              style={{
                transform: `scale(${1 + lipSyncLevel * 0.03})`,
                transition: 'transform 50ms ease-out',
              }}
            />
            
            {/* Lip Sync Glow Overlay */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: isPlaying 
                  ? `linear-gradient(to top, rgba(255,165,0,${lipSyncLevel * 0.2}) 0%, transparent 40%)`
                  : 'none',
              }}
            />

            {/* Speaking Indicator - Animated mouth effect */}
            {isPlaying && (
              <div className="absolute bottom-[22%] left-1/2 transform -translate-x-1/2">
                <div 
                  className="bg-gradient-to-b from-red-700/50 to-red-900/50 rounded-full transition-all duration-50"
                  style={{
                    width: `${16 + lipSyncLevel * 28}px`,
                    height: `${3 + lipSyncLevel * 14}px`,
                    opacity: 0.6,
                    filter: 'blur(3px)',
                  }}
                />
              </div>
            )}
          </div>

          {/* Audio Visualizer Bars */}
          {isPlaying && (
            <div className="absolute -bottom-6 sm:-bottom-8 left-1/2 transform -translate-x-1/2 flex gap-0.5 sm:gap-1 items-end">
              {[...Array(7)].map((_, i) => {
                const baseHeight = 4 + Math.abs(3 - i) * 2;
                const height = Math.max(baseHeight, lipSyncLevel * 35 * (1 - Math.abs(3 - i) * 0.15));
                return (
                  <div
                    key={i}
                    className="w-1.5 sm:w-2 bg-gradient-to-t from-orange-500 to-amber-400 rounded-full transition-all duration-50"
                    style={{ height: `${height}px` }}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Avatar Info - Mobile optimized */}
      <div className="text-center pb-2 sm:pb-4 px-4">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1">
          {avatar?.display_name}
        </h2>
        <p className="text-white/60 text-xs sm:text-sm max-w-xs sm:max-w-md mx-auto line-clamp-2">
          {avatar?.description}
        </p>
        {(avatar?.is_life_master || avatar?.is_video_avatar) && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full border border-amber-500/30">
            <span className="text-amber-400 text-xs sm:text-sm">🎤 Custom Voice</span>
          </div>
        )}
      </div>

      {/* Call Controls - Mobile optimized */}
      <div className="p-4 sm:p-6 flex justify-center gap-4 sm:gap-6 pb-safe">
        <button
          onClick={toggleMute}
          className={`p-3 sm:p-4 rounded-full transition-all ${
            isMuted 
              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          data-testid="mute-btn"
        >
          {isMuted ? <VolumeX className="w-5 h-5 sm:w-6 sm:h-6" /> : <Volume2 className="w-5 h-5 sm:w-6 sm:h-6" />}
        </button>
        
        <button
          onClick={onClose}
          className="p-3 sm:p-4 rounded-full bg-red-500 hover:bg-red-600 text-white transition-all shadow-lg shadow-red-500/30 active:scale-95"
          data-testid="end-call-btn"
        >
          <PhoneOff className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        onEnded={() => {
          setLipSyncLevel(0);
          onAudioEnd?.();
        }}
        onPlay={() => {
          if (audioContextRef.current?.state === 'suspended') {
            audioContextRef.current.resume();
          }
        }}
        playsInline
        preload="auto"
      />

      {/* Connecting Animation Overlay */}
      {!isConnected && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="text-center p-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white text-base sm:text-lg">Connecting to {avatar?.display_name}...</p>
            <p className="text-white/60 text-xs sm:text-sm mt-2">Tap anywhere if audio doesn't start</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCallMode;
